import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { getActiveNearExpiry } from '../services/api';
import { useAllCategories, useAllBrands } from '../hooks/useAllProductsQueries';
import { useCart } from '../contexts/CartContext';
import { toast } from 'react-hot-toast';
import { ShoppingCart, Check, Loader2, Clock } from 'lucide-react';
import { getImageUrl } from '../lib/utils';

const NearExpiryProductCard = ({ product, onAddToCart, isInCartAlready, isAddingToCart }) => {
  const formatExpiryDate = (dateString) => {
    if (!dateString) return 'N/A';
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric'
    });
  };

  const getDaysUntilExpiry = (expiryDate) => {
    if (!expiryDate) return null;
    const now = new Date();
    const expiry = new Date(expiryDate);
    const diffTime = expiry - now;
    return Math.ceil(diffTime / (1000 * 60 * 60 * 24));
  };

  const getDiscountDisplay = (discountType, discountValue) => {
    if (discountType === 'percentage') {
      return `${discountValue}% OFF`;
    }
    return `Rs. ${discountValue} OFF`;
  };

  const getNearExpiryPrice = (regularPrice, discountType, discountValue) => {
    if (!discountValue || !regularPrice) return regularPrice;
    if (discountType === 'percentage') {
      return Math.round(regularPrice * (1 - discountValue / 100));
    }
    return regularPrice - discountValue;
  };

  const daysLeft = getDaysUntilExpiry(product.expiryDate);
  const discountedPrice = getNearExpiryPrice(product.regularPrice, product.nearExpiryDiscountType, product.nearExpiryDiscountValue);
  const isOutOfStock = (product.stock ?? 0) <= 0;
  const urgencyColor = daysLeft === null ? 'bg-gray-500' : daysLeft <= 7 ? 'bg-red-500' : daysLeft <= 30 ? 'bg-yellow-500' : 'bg-green-500';

  return (
    <div className="group bg-white rounded-2xl overflow-hidden shadow-[0_2px_12px_rgba(0,0,0,0.04)] hover:shadow-lg transition-shadow duration-300 border border-gray-100 flex flex-col h-full">
      <Link to={`/product/${product.slug}`} className="block relative aspect-square bg-white p-2">
        <img
          src={product.images?.[0] ? getImageUrl(product.images[0]) : 'https://placehold.co/300x300/f8f9fa/a0aec0?text=No+Image'}
          alt={product.name}
          className="w-full h-full object-contain mix-blend-multiply"
          loading="lazy"
        />
        <div className="absolute top-2 left-2 bg-orange-500 text-white text-[10px] sm:text-[11px] font-bold px-2 sm:px-2.5 py-1 rounded-[4px] shadow-sm tracking-wide flex items-center gap-1">
          <Clock className="w-3 h-3" />
          Near Expiry
        </div>
        {daysLeft !== null && (
          <div className={`absolute top-2 right-2 text-white text-[9px] sm:text-[10px] font-bold px-1.5 sm:px-2 py-1 rounded-[4px] shadow-sm ${urgencyColor}`}>
            {daysLeft > 0 ? `${daysLeft}d left` : 'Expired'}
          </div>
        )}
      </Link>

      <div className="flex-grow flex flex-col p-2.5 sm:p-3 gap-1">
        <Link to={`/product/${product.slug}`}>
          <h3 className="text-xs sm:text-sm font-bold text-black line-clamp-1 hover:text-gray-700 transition-colors">
            {product.name}
          </h3>
        </Link>

        <div className="flex items-baseline gap-1.5">
          <span className="text-sm sm:text-[16px] font-bold text-[#111827] leading-none">
            Rs.{discountedPrice?.toLocaleString()}
          </span>
          <span className="text-[10px] sm:text-[11px] text-[#6B7280] line-through font-medium">
            Rs.{product.regularPrice?.toLocaleString()}
          </span>
        </div>

        <div className="text-[10px] sm:text-[11px] font-bold text-orange-600">
          {getDiscountDisplay(product.nearExpiryDiscountType, product.nearExpiryDiscountValue)}
        </div>

        <div className="text-[10px] sm:text-[11px] text-gray-500 flex items-center gap-1">
          <Clock className="w-3 h-3 flex-shrink-0" />
          Expires {formatExpiryDate(product.expiryDate)}
        </div>

        <button
          onClick={(e) => onAddToCart(product, e)}
          disabled={isOutOfStock || isAddingToCart}
          className="mt-auto pt-1.5 w-full h-7 sm:h-8 rounded-[6px] bg-[#1B365D] text-white text-[10px] sm:text-[11px] font-bold flex items-center justify-center gap-1.5 hover:bg-[#152A4A] transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
        >
          {isOutOfStock ? (
            'Out of Stock'
          ) : isAddingToCart ? (
            <Loader2 className="w-4 h-4 animate-spin" />
          ) : isInCartAlready ? (
            <>
              <Check className="w-3.5 h-3.5" /> In Cart
            </>
          ) : (
            <>
              <ShoppingCart className="w-3.5 h-3.5" /> Add to Cart
            </>
          )}
        </button>
      </div>
    </div>
  );
};

const NearExpiryPage = () => {
  const [products, setProducts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [pagination, setPagination] = useState({
    currentPage: 1,
    totalPages: 1,
    totalProducts: 0,
    hasNextPage: false,
    hasPrevPage: false
  });
  const [filters, setFilters] = useState({
    category: '',
    brand: '',
    sortBy: 'expiryDate',
    sortOrder: 'asc',
    page: 1,
    limit: 20
  });
  const [cartLoadingStates, setCartLoadingStates] = useState({});
  
  const { addToCart, isInCart } = useCart();

  const { data: categories = [] } = useAllCategories();
  const { data: brands = [] } = useAllBrands();

  useEffect(() => {
    fetchNearExpiryProducts();
  }, [filters]);

  const fetchNearExpiryProducts = async () => {
    try {
      setLoading(true);
      const response = await getActiveNearExpiry(filters);
      
      if (response.success) {
        setProducts(response.data.products || []);
        setPagination(response.data.pagination || {
          currentPage: 1,
          totalPages: 1,
          totalProducts: 0,
          hasNextPage: false,
          hasPrevPage: false
        });
      }
    } catch (error) {
      console.error('Error fetching near expiry products:', error);
      toast.error('Failed to load near expiry products');
    } finally {
      setLoading(false);
    }
  };

  const handleAddToCart = async (product, e) => {
    e.preventDefault();
    e.stopPropagation();
    
    const productId = product._id;

    // Guests can add to cart freely - addToCart keeps a local cart for them
    // and it gets merged into their account once they log in.
    if (cartLoadingStates[productId]) return;
    
    setCartLoadingStates(prev => ({ ...prev, [productId]: true }));
    
    try {
      await addToCart(productId, 1);
      toast.success('Product added to cart!');
    } catch (error) {
      console.error('Error adding to cart:', error);
      toast.error('Failed to add item to cart');
    } finally {
      setCartLoadingStates(prev => ({ ...prev, [productId]: false }));
    }
  };

  const handleFilterChange = (key, value) => {
    setFilters(prev => ({
      ...prev,
      [key]: value,
      page: 1 // Reset to first page when filters change
    }));
  };

  const handlePageChange = (page) => {
    setFilters(prev => ({ ...prev, page }));
  };



  return (
    <div className="min-h-screen bg-gray-50 dark:bg-gray-900">
      {/* Hero Section */}
      <div className="relative bg-gradient-to-br from-orange-50 to-red-50 dark:from-orange-900/20 dark:to-red-900/20">
        <div className="absolute inset-0 bg-gradient-to-r from-orange-100/50 to-red-100/50 dark:from-orange-900/10 dark:to-red-900/10"></div>
        <div className="relative container mx-auto px-4 py-16 md:py-24">
          <div className="text-center max-w-4xl mx-auto">
            {/* Main Heading */}
            <h1 className="text-3xl md:text-4xl lg:text-6xl font-bold text-gray-900 mb-6 font-heading leading-tight">
              Near Expiry
              <span className="block text-orange-600 dark:text-orange-400">Special Offers</span>
            </h1>
            
            {/* Subheading */}
            <p className="text-lg md:text-xl text-gray-600 dark:text-gray-300 mb-8 max-w-2xl mx-auto">
              Don't miss out on amazing discounts! Products nearing expiry with incredible savings.
            </p>
            
            {/* Stats */}
            <div className="flex flex-wrap justify-center gap-6 text-sm text-gray-500 dark:text-gray-400">
              <div className="flex items-center gap-2">
                <Clock className="w-4 h-4" />
                <span>Limited Time</span>
              </div>
              <div className="flex items-center gap-2">
                <span className="w-2 h-2 bg-orange-500 rounded-full"></span>
                <span>Fresh Products</span>
              </div>
              <div className="flex items-center gap-2">
                <span className="w-2 h-2 bg-red-500 rounded-full"></span>
                <span>Best Prices</span>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Filters and Products */}
      <div className="container mx-auto px-4 py-12">
        {/* Filters */}
        <div className="bg-white dark:bg-gray-800 rounded-lg shadow-sm p-6 mb-8">
          <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
            {/* Category Filter */}
            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                Category
              </label>
              <select
                value={filters.category}
                onChange={(e) => handleFilterChange('category', e.target.value)}
                className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md focus:ring-2 focus:ring-primary-500 focus:border-primary-500 dark:bg-gray-700 dark:text-white"
              >
                <option value="">All Categories</option>
                {categories.map((category) => (
                  <option key={category._id} value={category._id}>
                    {category.name}
                  </option>
                ))}
              </select>
            </div>

            {/* Brand Filter */}
            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                Brand
              </label>
              <select
                value={filters.brand}
                onChange={(e) => handleFilterChange('brand', e.target.value)}
                className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md focus:ring-2 focus:ring-primary-500 focus:border-primary-500 dark:bg-gray-700 dark:text-white"
              >
                <option value="">All Brands</option>
                {brands.map((brand) => (
                  <option key={brand._id} value={brand._id}>
                    {brand.name}
                  </option>
                ))}
              </select>
            </div>

            {/* Sort By */}
            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                Sort By
              </label>
              <select
                value={filters.sortBy}
                onChange={(e) => handleFilterChange('sortBy', e.target.value)}
                className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md focus:ring-2 focus:ring-primary-500 focus:border-primary-500 dark:bg-gray-700 dark:text-white"
              >
                <option value="expiryDate">Expiry Date</option>
                <option value="nearExpiryDiscountValue">Discount Amount</option>
                <option value="newest">Newest First</option>
              </select>
            </div>

            {/* Sort Order */}
            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                Order
              </label>
              <select
                value={filters.sortOrder}
                onChange={(e) => handleFilterChange('sortOrder', e.target.value)}
                className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md focus:ring-2 focus:ring-primary-500 focus:border-primary-500 dark:bg-gray-700 dark:text-white"
              >
                <option value="asc">Ascending</option>
                <option value="desc">Descending</option>
              </select>
            </div>
          </div>
        </div>

        {/* Products Grid */}
        {loading ? (
          <div className="flex justify-center items-center py-12">
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary-500"></div>
          </div>
        ) : products.length === 0 ? (
          <div className="text-center py-12">
            <div className="text-gray-500 dark:text-gray-400 text-lg mb-4">
              No near expiry products found
            </div>
            <p className="text-gray-400 dark:text-gray-500">
              Try adjusting your filters or check back later for new offers.
            </p>
          </div>
        ) : (
          <>
            <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-6 mb-8">
              {products.map((product) => (
                <NearExpiryProductCard
                  key={product._id}
                  product={product}
                  onAddToCart={handleAddToCart}
                  isInCartAlready={isInCart(product._id)}
                  isAddingToCart={!!cartLoadingStates[product._id]}
                />
              ))}
            </div>

            {/* Pagination */}
            {pagination.totalPages > 1 && (
              <div className="flex justify-center items-center space-x-2">
                <button
                  onClick={() => handlePageChange(pagination.currentPage - 1)}
                  disabled={!pagination.hasPrevPage}
                  className="px-4 py-2 text-sm font-medium text-gray-500 bg-white border border-gray-300 rounded-md hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed dark:bg-gray-800 dark:border-gray-600 dark:text-gray-300 dark:hover:bg-gray-700"
                >
                  Previous
                </button>
                
                <span className="px-4 py-2 text-sm text-gray-700 dark:text-gray-300">
                  Page {pagination.currentPage} of {pagination.totalPages}
                </span>
                
                <button
                  onClick={() => handlePageChange(pagination.currentPage + 1)}
                  disabled={!pagination.hasNextPage}
                  className="px-4 py-2 text-sm font-medium text-gray-500 bg-white border border-gray-300 rounded-md hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed dark:bg-gray-800 dark:border-gray-600 dark:text-gray-300 dark:hover:bg-gray-700"
                >
                  Next
                </button>
              </div>
            )}
          </>
        )}
      </div>
    </div>
  );
};

export default NearExpiryPage;
